import { Link } from 'react-router-dom';

const LAST_UPDATED = '25 October 2026';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl md:text-2xl font-bold text-[var(--primary)] mb-3">{title}</h2>
    <div className="text-[var(--text-secondary)] leading-relaxed space-y-3">{children}</div>
  </section>
);

const PrivacyPolicy = () => (
  <main data-testid="privacy-page">
    <section className="page-header" data-testid="privacy-header">
      <div className="container-main">
        <div className="breadcrumb">
          <Link to="/">Our Services</Link>
          <span>/</span>
          <span className="text-white">Privacy Policy</span>
        </div>
        <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4 opacity-0 animate-fade-in-up">Privacy Policy</h1>
      </div>
    </section>

    <section className="section-padding bg-white">
      <div className="container-main max-w-3xl">
        <p className="text-sm text-[var(--text-secondary)] mb-10">Last updated: {LAST_UPDATED}</p>

        <Section title="Who we are">
          <p>
            DAAS Global Limited ("DAAS Global", "we", "us") is an international student education consultancy and a subsidiary of DAAS Limited,
            based at Rupayan Shopping Square (4th Floor), Plot No. 02, Sayem Sobhan Anvir Road, Bashundhara R/A, Dhaka-1229, Bangladesh.
            We provide advisory and administrative support only. This policy explains what personal information we collect, why, and how we look after it.
          </p>
        </Section>

        <Section title="What we collect">
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Free profile evaluation:</strong> your name, email address, phone or WhatsApp number, and any documents you choose to upload (for example transcripts, certificates, English test results or a passport copy).</li>
            <li><strong>Counselling and applications:</strong> information you share with our counsellors, such as your education history, grades, English test scores, work experience, preferred programs, countries and intakes.</li>
            <li><strong>Career applications:</strong> your name, contact details, CV and anything else you include in your application.</li>
            <li><strong>Messages:</strong> what you send us by email, WhatsApp, phone or social media.</li>
          </ul>
          <p>
            <strong>Website statistics:</strong> we use Google Analytics to understand how visitors use this website, for example which pages are viewed,
            how visitors found us, and their device and approximate location (country or city). It uses cookies for this. It does not give us your name or
            contact details, and we do not use advertising cookies. You can block it with your browser's cookie settings or Google's opt-out add-on
            (tools.google.com/dlpage/gaoptout).
          </p>
        </Section>

        <Section title="How we use your information">
          <ul className="list-disc pl-6 space-y-2">
            <li>To prepare your free profile evaluation and contact you about it.</li>
            <li>To counsel you and, when you ask us to, prepare and submit applications to universities and colleges and help with visa administrative requirements.</li>
            <li>To consider you for a job, if you apply to work with us.</li>
            <li>To keep accurate records of our services and meet our legal and accounting obligations.</li>
          </ul>
          <p>We do not sell your personal information, and we do not use it for advertising by others.</p>
        </Section>

        <Section title="Service providers and AI">
          <p>
            We use trusted service providers to run our forms, store files and manage our work, including Google services (such as Google Forms, Google Drive
            Google Sheets and Google Analytics) and our internal management system. They process information for us and only as needed to provide their services.
          </p>
          <p>
            To help prepare profile evaluations, we may use Google's Gemini AI to research programs and entry requirements. We only send it your academic
            details (such as qualifications, grades and English scores) and your study preferences — never your name, phone number, email address or documents.
            A DAAS Global counsellor reviews every evaluation before it is sent to you.
          </p>
        </Section>

        <Section title="When we share your information">
          <ul className="list-disc pl-6 space-y-2">
            <li>With universities, colleges and their application systems, only when you ask us to apply on your behalf.</li>
            <li>With other parties you ask us to deal with for you, such as accommodation or scholarship providers.</li>
            <li>When the law requires it.</li>
          </ul>
        </Section>

        <Section title="How long we keep it">
          <p>
            We keep your information while we are helping you and for up to three years after our last contact, so we can answer questions about your file
            and keep proper records, unless the law requires us to keep it longer. You can ask us to delete it sooner (see below).
          </p>
        </Section>

        <Section title="Keeping it safe">
          <p>
            Access to your information is limited to DAAS Global staff who need it for their work, using password-protected accounts. No system is completely
            secure, but we take reasonable steps to protect your information.
          </p>
        </Section>

        <Section title="Your choices and rights">
          <p>
            You can ask us to show you the information we hold about you, correct it, delete it, or stop contacting you. Email us at{' '}
            <a href="mailto:daasglobalsub@gmail.com" className="text-[var(--secondary)] underline">daasglobalsub@gmail.com</a> and we will reply within 30 days.
          </p>
        </Section>

        <Section title="Students under 18">
          <p>If you are under 18, please ask a parent or guardian to read this policy and to be involved when you share your information with us.</p>
        </Section>

        <Section title="Changes to this policy">
          <p>We may update this policy from time to time. The latest version is always on this page, with the date it was last updated.</p>
        </Section>

        <Section title="Contact us">
          <p>
            DAAS Global Limited, Rupayan Shopping Square (4th Floor), Plot No. 02, Sayem Sobhan Anvir Road, Bashundhara R/A, Dhaka-1229, Bangladesh.<br />
            Email: <a href="mailto:daasglobalsub@gmail.com" className="text-[var(--secondary)] underline">daasglobalsub@gmail.com</a>
          </p>
        </Section>
      </div>
    </section>
  </main>
);

export default PrivacyPolicy;
